import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { BetSlipType } from '@bpos';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Store } from '@ngrx/store';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';
import { PickSourceProvider } from 'packages/sports/web/app/src/option-pick/pick-source.provider';
import { Observable, combineLatest, map, tap, withLatestFrom } from 'rxjs';

import { AlertType } from '../../../alerts/models';
import { RewardTokensActions } from '../../../reward-tokens/actions';
import { CriteriaType } from '../../../reward-tokens/reward-tokens.model';
import { selectTokensStateContext } from '../../../reward-tokens/selectors';
import { getSelectedTokenAndEligibilityForContext } from '../../../reward-tokens/services/linear-reward-tokens.utils';
import { IRewardTokenEligibilityState, TokensRecord } from '../../../reward-tokens/state';
import { IBetslipTypeState } from '../../../types/state';
import { selectQuickBetContext, selectQuickBetPickNotificationState } from '../../quick-bet.selectors';
import { QuickBetErrorTrackingService } from '../../services/quick-bet-error-tracking.service';
import { QuickBetStakeStatusService } from '../../services/quick-bet-stake-status.service';
import { QuickBetUtils } from '../../services/quick-bet-utils.service';

interface QuickBetNotification {
    message: string;
    title: string | null;
    oddsChanged: boolean;
}

@Component({
    selector: 'bs-quick-bet-pick-notification',
    templateUrl: './quick-bet-pick-notification.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-pick-notification.component.scss'],
    standalone: false,
})
export class QuickBetPickNotificationComponent implements OnInit {
    readonly isEpcotEnabled = this.epcotConfigService.isEnabled();

    private criteriaTypeNames = {
        [BetSlipType.Single]: this.sitecore.crmTokens.errors.SlipTypeSingle,
        [BetSlipType.Combo]: this.sitecore.crmTokens.errors.SlipTypeCombo,
        [BetSlipType.System]: this.sitecore.crmTokens.errors.SlipTypeSystem,
    };

    alertType = AlertType.Information;
    private notification$: Observable<QuickBetNotification | null>;
    private tokenValidityError$: Observable<string | null>;
    errors$: Observable<{ notification: QuickBetNotification | null; tokenError: string | null }>;

    constructor(
        public sitecore: Sitecore,
        private quickBetUtils: QuickBetUtils,
        private store: Store,
        private quickBetErrorTrackingService: QuickBetErrorTrackingService,
        private pickSourceProvider: PickSourceProvider,
        private epcotConfigService: EpcotConfigService,
        private moneyService: MoneyService,
        private quickBetStakeStatusService: QuickBetStakeStatusService,
    ) {}

    ngOnInit(): void {
        this.tokenValidityError$ = this.store.select(selectTokensStateContext).pipe(
            withLatestFrom(this.store.select(selectQuickBetContext)),
            map(([{ eligibilityState, tokens, types }, quickBetContext]) =>
                this.getTokensValidityErrorMessage(eligibilityState, tokens, types, quickBetContext),
            ),
        );

        this.notification$ = combineLatest([
            this.store.select(selectQuickBetPickNotificationState),
            this.quickBetStakeStatusService.isQuickBetStakeActive$,
        ]).pipe(
            map(
                ([
                    {
                        quickBet,
                        picksList,
                        oddsAcceptance,
                        errorState: { oddsChanged, isLocked },
                    },
                    isStakeActive,
                ]) => {
                    const oddsAccepted = this.quickBetUtils.areOddsAccepted(picksList, quickBet.pickIds, oddsAcceptance);
                    if (!oddsAccepted) {
                        oddsChanged = isStakeActive;
                    }

                    const message =
                        oddsChanged || quickBet.oddsChangedOnBetPlacement
                            ? this.sitecore.quickBetMessages.OddsChanged
                            : isLocked || quickBet.pickLockedOnBetPlacement
                              ? this.sitecore.quickBetMessages.PickAvailabilityChanged
                              : null;

                    const pickChangedOnBetPlacement = quickBet.pickLockedOnBetPlacement || quickBet.oddsChangedOnBetPlacement;
                    const title = pickChangedOnBetPlacement ? this.sitecore.quickBetMessages.BetPlaceFailedTitle : null;

                    return message ? { message, title, oddsChanged: oddsChanged || isLocked } : null;
                },
            ),
            tap((notification) => {
                this.quickBetErrorTrackingService.offerChange = notification ? notification.oddsChanged : false;
            }),
        );

        this.errors$ = combineLatest([this.notification$, this.tokenValidityError$]).pipe(
            map(([notification, tokenError]) => ({ notification, tokenError })),
        );
    }

    private getTokensValidityErrorMessage = (
        eligibilityState: IRewardTokenEligibilityState,
        tokens: TokensRecord,
        types: IBetslipTypeState,
        quickBetContext: ReturnType<typeof selectQuickBetContext>,
    ): string | null => {
        const [selectedToken, tokenEligibility] = getSelectedTokenAndEligibilityForContext(quickBetContext, eligibilityState, tokens, types);

        if (!selectedToken || !tokenEligibility) {
            return null;
        }

        if (tokenEligibility.softCriteriasValidity[CriteriaType.SlipType] === false) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.errors.QuickBetSlipTypeCriteriaNotMet, {
                criteria: this.criteriaTypeNames[selectedToken.filter.betSlipType!],
            });
        }

        if (tokenEligibility.softCriteriasValidity[CriteriaType.MinimumLegs] === false) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.errors.MinimumLegs, {
                minLegs: selectedToken.minimumLegs!,
            });
        }

        if (tokenEligibility.softCriteriasValidity[CriteriaType.MaximumStake] === false) {
            const maximumStakeString = this.moneyService.transform(selectedToken.maximumStake, true);

            return interpolateDoubleBracesString(this.sitecore.crmTokens.errors.MaximumStakeError, {
                maxStake: maximumStakeString,
            });
        }

        return null;
    };

    messageClick(event: MouseEvent): void {
        if (event && event.target) {
            if ((<Element>event.target).className.includes('remove-token')) {
                this.store.dispatch(RewardTokensActions.deselectAllToken({ trackingSource: this.pickSourceProvider.getTracking() }));
            }
        }
    }
}
